import { ErrorsOr, flatMapErrorsOrK } from "@me8eon/errors";
import { AxiosStatic } from "axios";
import { AsyncStore } from "@me8eon/async_storage";
import { Codec } from "@me8eon/codec";

/** Fetches a fresh JWT to authenticate against the backend proxy. Returns undefined if the user isn't logged in. */
export type ProxyJwtTokenFn = () => Promise<string | undefined>;

export type ProxyBlobConfig<ID, T> = {
    /** Base URL of the backend, e.g. EnvironmentConfig.nodeService. The blob is proxied via `${base}/service/recentChat/${blobNameFn(id)}` */
    base: string
    jwtTokenFn: ProxyJwtTokenFn
    blobNameFn: (id: ID) => string
    codec: Codec<T>
    axios: AxiosStatic
}

export type ProxyLoadBlobStorageConfig<ID, T> = ProxyBlobConfig<ID, T> & {
    defaultValue: (id: ID) => Promise<ErrorsOr<T>>
}

export type ProxyBlobStorageConfig<ID, T> = ProxyLoadBlobStorageConfig<ID, T>

export function proxyBlobUrl<ID, T>({ base, blobNameFn }: ProxyBlobConfig<ID, T>, id: ID): string {
    return `${base}/service/recentChat/${blobNameFn(id)}`;
}

async function authHeaders(jwtTokenFn: ProxyJwtTokenFn): Promise<Record<string, string>> {
    const token = await jwtTokenFn();
    return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
}

export function proxyBlobStore<ID, T>(config: ProxyBlobConfig<ID, T>) {
    const { axios, jwtTokenFn, codec } = config;
    return async (id: ID, value: T): Promise<ErrorsOr<void>> => {
        return flatMapErrorsOrK(codec.encode(value), async (s: string): Promise<ErrorsOr<void>> => {
            const url = proxyBlobUrl(config, id);
            try {
                const response = await axios.put(url, { data: s }, {
                    headers: await authHeaders(jwtTokenFn),
                    validateStatus: () => true,
                });

                if (response.status >= 400) {
                    console.error(`❌ storing blob contents for ${id} failed:`, response.status, response.data);
                    return {
                        errors: [`Failed to store blob for ${id}: ${response.status} ${JSON.stringify(response.data)}`],
                    };
                }

                return { value: undefined };
            } catch (e: any) {
                console.error(`⚠️ Error storing blob for ${id}:`, e);
                return {
                    errors: [`Error storing blob for ${id}: ${e.message}`],
                    extras: e,
                };
            }
        });
    };
}

export function proxyBlobLoad<ID, T>(config: ProxyLoadBlobStorageConfig<ID, T>) {
    const { axios, jwtTokenFn, defaultValue, codec } = config;
    return async (id: ID, undefinedIfNotIn?: boolean): Promise<ErrorsOr<T>> => {
        const url = proxyBlobUrl(config, id);
        try {
            const response = await axios.get(url, {
                responseType: "text",
                headers: await authHeaders(jwtTokenFn),
                validateStatus: () => true,
            });

            if (response.status === 404) {
                return undefinedIfNotIn ? { value: undefined as T } : defaultValue(id);
            }

            if (response.status >= 400) {
                console.error(`❌ Failed to load blob for ${id}:`, response.status, response.data);
                return {
                    errors: [`Failed to load blob for ${id}: ${response.status} ${JSON.stringify(response.data)}`],
                };
            }

            return codec.decode(response.data);
        } catch (e: any) {
            console.error(`⚠️ Error loading blob for ${id}:`, e);
            return {
                errors: [`Error loading blob for ${id}: ${e.message}`],
                extras: e,
            };
        }
    };
}

export const proxyBlobDelete = <ID, T>(config: ProxyBlobConfig<ID, T>) => async (id: ID): Promise<ErrorsOr<void>> => {
    const url = proxyBlobUrl(config, id);
    try {
        const response = await config.axios.delete(url, {
            headers: await authHeaders(config.jwtTokenFn),
            validateStatus: () => true,
        });
        if (![200, 202, 204, 404].includes(response.status)) {
            return { errors: [`Failed to delete blob id=${String(id)}: ${response.status}`] };
        }
        return { value: undefined };
    } catch (e: any) {
        return { errors: [`Failed to delete blob id=${String(id)}: ${e.message}`], extras: e };
    }
};

/**
 * Same shape as `azureBlobStorage`, but instead of talking to Azure Blob Storage directly with a
 * client-side SAS token, it goes through the backend's `/service/recentChat/*` proxy routes, which hold
 * the storage credentials server-side. Use this instead of `azureBlobStorage` whenever the caller runs
 * in the browser.
 */
export function proxyBlobStorage<ID, T>(config: ProxyBlobStorageConfig<ID, T>): AsyncStore<ID, T> {
    return {
        store: proxyBlobStore(config),
        get: proxyBlobLoad(config),
        delete: proxyBlobDelete(config),
    };
}
